import { gameChannelName, queryKeys, useLiveGame } from '@ottd/api';
import type { GameDetail, GameStatus } from '@ottd/api';
import { useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { BevelWindow } from '@/components/prototype/bevel-window';
import { ClimateThumb } from '@/components/prototype/climate-thumb';
import { ConnectDialog } from '@/components/prototype/connect-dialog';
import { GameStatusBadge } from '@/components/prototype/game-status-badge';
import { KeyValueRows } from '@/components/prototype/key-value-rows';
import type { KeyValueRow } from '@/components/prototype/key-value-rows';
import { QueryState } from '@/components/prototype/query-state';
import { RelativeTime } from '@/components/prototype/relative-time';
import { TypeTile } from '@/components/prototype/type-tile';
import { Skeleton } from '@/components/ui/skeleton';
import { useEchoChannel } from '@/hooks/use-echo-channel';
import { climateLabel } from '@/lib/climate';
import { formatSignedPoints } from '@/lib/format';
import { serverTypeTheme } from '@/lib/server-type';
import { CompanyCard } from '@/pages/game/company-card';
import { bannerStyle } from '@/pages/shared';

/** Tone per lifecycle state; the value itself is translated per render. */
const STATUS_TONE: Record<GameStatus, KeyValueRow['tone']> = {
  live: 'green',
  ended: 'gold',
  cancelled: 'orange',
};

/** `game:info.status*` key per lifecycle state. */
const STATUS_KEY: Record<GameStatus, string> = {
  live: 'info.statusLive',
  ended: 'info.statusEnded',
  cancelled: 'info.statusCancelled',
};

/**
 * "Won in" (the winner's time) and "Lasted" (start to end on the wall clock,
 * legacy game page). A time anomaly - recorded times shorter than the in-game
 * calendar allows - says so in both rows instead of printing them.
 */
function timeRows(game: GameDetail, t: TFunction<'game'>): KeyValueRow[] {
  if (game.timeAnomaly) {
    const unavailable = t('info.timeUnavailable');
    return [
      { key: t('info.wonIn'), value: unavailable, tone: 'dim' },
      { key: t('info.lasted'), value: unavailable, tone: 'dim' },
    ];
  }

  return [
    ...(game.duration
      ? [{ key: t('info.wonIn'), value: game.duration, tone: 'gold' as const }]
      : []),
    ...(game.lasted ? [{ key: t('info.lasted'), value: game.lasted }] : []),
  ];
}

export default function GamePage() {
  const { t } = useTranslation('game');
  const { id: idParam } = useParams();
  const parsed = Number(idParam);
  const id = Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;

  const queryClient = useQueryClient();
  const invalidate = () => {
    if (id !== undefined) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.live.game(id) });
    }
  };

  // Subscribe to the per-game channel; poll as the fallback when realtime is off.
  const connected = useEchoChannel(id !== undefined ? gameChannelName(id) : null, {
    'game.event': invalidate,
    'game.snapshot': invalidate,
  });

  const { data: game, isLoading, isError, error } = useLiveGame(id, connected ? false : 5000);

  // The banner sits on the dark type tint, so its text comes from the type's
  // own over-image palette rather than the page tokens (which invert by theme).
  const banner = serverTypeTheme(game?.type);

  const infoRows: KeyValueRow[] = game
    ? [
        {
          key: t('info.status'),
          value: t(STATUS_KEY[game.status]),
          tone: STATUS_TONE[game.status],
        },
        {
          key: t('info.server'),
          value:
            game.serverId !== null ? (
              <Link to={`/server/${game.serverId}`} className="text-orange">
                {game.server}
              </Link>
            ) : (
              game.server
            ),
        },
        { key: t('info.address'), value: game.address ?? '-', tone: 'orange' },
        { key: t('info.script'), value: game.script },
        { key: t('info.goal'), value: game.goal, tone: 'green' },
        { key: t('info.mapSize'), value: game.mapSize },
        { key: t('info.climate'), value: climateLabel(game.climate) ?? '-' },
        { key: t('info.started'), value: <RelativeTime iso={game.startedAt} /> },
        ...(game.status !== 'live'
          ? [
              {
                key: game.status === 'cancelled' ? t('info.cancelled') : t('info.ended'),
                value: <RelativeTime iso={game.endedAt} />,
              },
            ]
          : []),
        ...timeRows(game, t),
        { key: t('info.inGameDate'), value: game.inGameDate, tone: 'default' },
      ]
    : [];

  // A malformed `:id` (non-numeric / non-positive) disables the query, so it
  // would otherwise render neither loading, error, nor content - a blank page.
  // Guard it explicitly. (All hooks above run unconditionally first.)
  if (id === undefined) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 py-10 sm:px-5">
        <p className="text-dim font-mono text-sm">{t('detail.notFound')}</p>
      </div>
    );
  }

  return (
    <QueryState
      isLoading={isLoading}
      isError={isError}
      error={error}
      skeleton={
        <div className="mx-auto max-w-[1280px] px-4 py-8 sm:px-5">
          <Skeleton className="h-40 w-full" />
        </div>
      }
    >
      {game ? (
        <>
          <div className="border-b-2 border-b-bev-d" style={bannerStyle(game.type, '100% 75%')}>
            <div className="mx-auto flex max-w-[1280px] flex-wrap items-center gap-4 px-4 py-6 sm:px-5">
              <TypeTile type={game.type} size={58} />
              <ClimateThumb climate={game.climate} width={84} className="hidden sm:block" />
              <div className="min-w-0">
                <p className="font-mono text-[11px]" style={{ color: banner.subColor }}>
                  <Link to="/game" style={{ color: banner.linkColor }}>
                    {t('detail.breadcrumb')}
                  </Link>{' '}
                  / #{game.id}
                </p>
                <h1
                  className="font-display text-xl text-white sm:text-2xl"
                  style={{ textShadow: '2px 2px 0 #000' }}
                >
                  {t('detail.heading', { server: game.server, id: game.id })}
                </h1>
                <p className="font-mono text-xs" style={{ color: banner.subColor }}>
                  {t('detail.subtitle', {
                    script: game.script,
                    goal: game.goal,
                    map: game.mapSize,
                  })}
                  {climateLabel(game.climate) ? ` · ${climateLabel(game.climate)}` : ''}
                </p>
              </div>
              <div className="ml-auto flex flex-col items-end gap-1.5">
                <GameStatusBadge status={game.status} />
                <span className="font-mono text-xs text-white">
                  {t('detail.inGame', { date: game.inGameDate })}
                </span>
                {game.status === 'live' && game.startedAt ? (
                  <span className="font-mono text-[11px]" style={{ color: banner.subColor }}>
                    {t('detail.started')} <RelativeTime iso={game.startedAt} />
                  </span>
                ) : null}
                {game.status === 'live' ? (
                  <ConnectDialog
                    address={game.address}
                    inviteCode={game.inviteCode}
                    serverName={game.server}
                  />
                ) : null}
              </div>
            </div>
          </div>

          <div className="mx-auto grid max-w-[1280px] gap-4 px-4 py-6 sm:px-5 lg:grid-cols-[1fr_360px]">
            <div className="flex flex-col gap-4">
              {game.companies.length === 0 ? (
                <p className="text-dim font-mono text-sm">{t('detail.noCompanies')}</p>
              ) : (
                game.companies.map((company) => (
                  <CompanyCard
                    key={company.name}
                    company={company}
                    type={game.type}
                    goal={game.goal}
                    status={game.status}
                  />
                ))
              )}
            </div>

            <aside className="flex flex-col gap-4">
              <BevelWindow title={t('detail.infoTitle')}>
                <KeyValueRows rows={infoRows} />
              </BevelWindow>

              <BevelWindow title={t('detail.stakeTitle')} variant="orange">
                {game.pointsAtStake.length > 0 ? (
                  <ul className="flex flex-col gap-1.5">
                    {game.pointsAtStake.map((stake, index) => (
                      <li
                        key={index}
                        className="flex items-center justify-between font-mono text-[13px]"
                      >
                        <span className="text-dim uppercase">{stake.place}</span>
                        <span className="text-gold">{formatSignedPoints(stake.pts)} PTS</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-dim font-mono text-[13px]">{t('detail.stakeEmpty')}</p>
                )}
              </BevelWindow>
            </aside>
          </div>
        </>
      ) : null}
    </QueryState>
  );
}
